import React, { FC, useCallback, useEffect, useRef } from 'react';
import { Formik } from 'formik';
import classNames from 'classnames';

import { LoadingPage, ProcessingModal, useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import {
  ScreenHeader,
  Footer,
  FooterNextButton,
  FooterPreviousButton,
  Body,
} from 'QuoteApp/components';
import { Disclosures } from 'QuoteApp/services/endpoints/GetDisclosures';
import {
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import { getErrorMessage } from 'QuoteApp/utils';
import { useFooter } from 'QuoteApp/utils/hooks';
import { useAuth0 } from 'Auth';
import {
  ESignatureErrorsType,
  PurchaseProvider,
  useAppContext,
  usePurchaseContext,
} from 'QuoteApp/services/providers';
import {
  MainContent,
  ProcessOneModal,
  PurchaseFormValuesType,
  SidebarContent,
} from '.';
import { AgentSignatureContent } from './AgentSignatureContent';
import { PurchaseAlertBanners } from './PurchaseAlertBanners';
import { PurchaseDisclaimer } from './PurchaseDisclaimer';
import styles from './styles.module.css';

export const Purchase: FC = () => {
  const initialRef = useRef(true);
  const { toast } = useToast();
  const { previousStep } = useFooter(5);

  const {
    appDataStale,
    isEditingContactInfo,
    paymentDetails,
    processing,
    eSignatureSMSConsent,
    setESignatureErrors,
    scrollIntoESignatureView,
    agentConfirmation,
    signaturePacketSendAttempt,
    postConfirmation,
  } = usePurchaseContext();

  const { user } = useAuth0();
  const { appId, details, config, setDetails, refreshRate } = useAppContext();

  // TODO: Pull from the configuration response - https://clearcover.atlassian.net/browse/RVL-1492
  const isOchoAgent = user?.partner?.name === 'Ocho Insurance Solutions LLC';

  // This is part of a short-term fix to support Bind API and AS 2.0
  // For now Ocho is the only partner that uses AgentPay
  const isOchoPolicy = paymentDetails?.paymentProvider === 'agentPay';
  const agentSignature2FeatureFlag = !!details.commands.sendSignaturePacket;
  const isPaymentFlexibilityEnabled = !!config?.flexiblePayments.enabled;

  const isDocumentSigningHandledByPartner =
    agentSignature2FeatureFlag && isOchoAgent;
  const policyDocumentsSigned = !details.commands.sendSignaturePacket?.runnable;
  const hasPolicyDocumentsBeenSigned =
    isDocumentSigningHandledByPartner && policyDocumentsSigned;

  const purchaseDisclosureDocument = details.disclosures.find(
    doc => doc.attributes.documentName === Disclosures.Purchase
  );

  const purchaseDisclosureCompleted = !!purchaseDisclosureDocument?.attributes
    .completed;

  const [, postDisclosure] = useLazyQuoteApi<GetDetailsResponseType>({
    method: 'POST',
    url: `/insurance_applications/${appId}/document_signatures/${Disclosures.Purchase}/complete`,
    transformResponse: transformAppDetails,
  });

  const getSubHeaderText = () => {
    if (isPaymentFlexibilityEnabled) {
      return 'Please select the preferred payment frequency and method below.';
    }
    return undefined;
  };

  const handleDisclosure = useCallback(async () => {
    // If there are payment errors this could trigger multiple times; only complete once
    if (!purchaseDisclosureCompleted) {
      try {
        const completedDisclosureData = await postDisclosure();

        // refresh updated details
        setDetails(completedDisclosureData.data);
      } catch (e) {
        toast({
          type: 'error',
          message: `Error capturing disclosure: ${getErrorMessage(
            e
          )}. Please try again.`,
          withSecondaryNav: false,
          icon: 'error',
        });

        // throw again to stop bind
        throw new Error('Failed to capture disclosure');
      }
    }
  }, [postDisclosure, purchaseDisclosureCompleted, setDetails, toast]);

  const handlePostAgentConfirmation = useCallback(async () => {
    if (agentConfirmation) {
      const completedDisclosureData = await postConfirmation();

      // refresh updated details
      setDetails(completedDisclosureData);
    }
  }, [postConfirmation, setDetails, agentConfirmation]);

  const handleSignaturePacketValidation = useCallback((): boolean => {
    let valid = true;
    const errors: ESignatureErrorsType = {};
    const signatureCompleted = isDocumentSigningHandledByPartner
      ? hasPolicyDocumentsBeenSigned
      : signaturePacketSendAttempt;

    if (
      !isDocumentSigningHandledByPartner &&
      eSignatureSMSConsent === undefined
    ) {
      errors.smsConsent =
        'Please select whether the customer would like to receive these documents via text message.';
      valid = false;
    }
    if (!signatureCompleted) {
      errors.sentSingnaturePacket =
        'Signature packet must be sent before completing purchase.';
      valid = false;
    }
    if (!agentConfirmation) {
      errors.agentConfirmation = 'Must check all agreements.';
      valid = false;
    }

    if (Object.keys(errors).length > 0) {
      setESignatureErrors(errors);
    }

    return valid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eSignatureSMSConsent,
    signaturePacketSendAttempt,
    agentConfirmation,
    isDocumentSigningHandledByPartner,
  ]);

  useEffect(() => {
    // force to refresh rate only run one time on-loading
    if (initialRef.current) {
      refreshRate();
    }
    return () => {
      initialRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!paymentDetails || appDataStale) {
    return (
      <LoadingPage
        message={
          appDataStale
            ? 'Please wait while we fetch the most recent application data...'
            : ''
        }
      />
    );
  }

  const paymentPlanFromPaymentPreferences = paymentDetails.paymentPlans.find(
    plan => plan.paymentPlanEnum === details.paymentPreferences.paymentPlanEnum
  );

  const defaultPaymentPlanFromDetails = paymentDetails.paymentPlans.find(
    plan => plan.identifier === paymentDetails.defaultPaymentPlan.identifier
  );

  const defaultPaymentPlan =
    paymentPlanFromPaymentPreferences ||
    defaultPaymentPlanFromDetails ||
    paymentDetails.paymentPlans[0];

  const disableNextButton =
    processing ||
    !details.commands.bind.runnable ||
    isEditingContactInfo ||
    isOchoPolicy;

  return (
    <Body data-testid="purchase-page" fullWidth>
      <Formik<PurchaseFormValuesType>
        enableReinitialize
        initialValues={{
          paymentMethod: defaultPaymentPlan.paymentMethodIdentifier,
          paymentPlanId: defaultPaymentPlan.identifier,
          quoteId: paymentDetails.quoteId,
          adyenForm: null,
          contactInfo: {
            [details.principalInsuredId]: {
              email:
                details.contactInfo[details.principalInsuredId].email.value,
              phoneNumber:
                details.contactInfo[details.principalInsuredId].phoneNumber
                  .value,
              phoneType:
                details.contactInfo[details.principalInsuredId].phoneType.value,
            },
          },
        }}
        onSubmit={async values => {
          // Form onSubmit handles Adyen Payments
          // ProcessOne is handled via ProcessOneModal in footer

          // if agentSignature2FeatureFlag is enabled, validate signature packet and post agent confirmation then post payment disclosure
          if (agentSignature2FeatureFlag) {
            const attemptToSendSignaturePacket = handleSignaturePacketValidation();
            if (!attemptToSendSignaturePacket) {
              scrollIntoESignatureView();
              return;
            }
            await handlePostAgentConfirmation();
          }

          await handleDisclosure();

          // final step submit adyenForm payment
          if (
            paymentDetails.paymentProvider === 'adyen' ||
            paymentDetails.paymentProvider === 'agentPay'
          ) {
            values.adyenForm?.submit();
          }
        }}
      >
        {({ submitForm, isSubmitting }) => {
          return (
            <>
              <div className={styles.container}>
                <ScreenHeader
                  headerText="Review & complete purchase"
                  subHeaderText={getSubHeaderText()}
                />
                <PurchaseAlertBanners />
                {processing && <ProcessingModal />}
                <div
                  className={classNames(
                    styles.bodyWrapper,
                    agentSignature2FeatureFlag
                      ? styles.agentSignatureBodyWrapper
                      : ''
                  )}
                >
                  <MainContent paymentDetails={paymentDetails} />
                  <SidebarContent paymentDetails={paymentDetails} />
                </div>
                {agentSignature2FeatureFlag && (
                  <AgentSignatureContent
                    policyDocumentsSigned={hasPolicyDocumentsBeenSigned}
                    documentSigningHandledByAgent={
                      isDocumentSigningHandledByPartner
                    }
                  />
                )}
              </div>
              <Footer
                hideRate
                disclaimer={<PurchaseDisclaimer />}
                previousButton={
                  <FooterPreviousButton
                    onClick={previousStep.onClickSuccess}
                    disabled={!!processing}
                  >
                    {previousStep.text}
                  </FooterPreviousButton>
                }
                nextButton={
                  paymentDetails.paymentProvider === 'processOne' ? (
                    <ProcessOneModal
                      paymentDetails={paymentDetails}
                      handleDisclosure={handleDisclosure}
                      handleSignaturePacketValidation={
                        handleSignaturePacketValidation
                      }
                      handlePostAgentConfirmation={handlePostAgentConfirmation}
                      disabled={disableNextButton}
                    />
                  ) : (
                    <FooterNextButton
                      onClick={() => {
                        submitForm();
                      }}
                      isSubmitting={isSubmitting || processing}
                      disabled={disableNextButton}
                    >
                      Complete Purchase
                    </FooterNextButton>
                  )
                }
              />
            </>
          );
        }}
      </Formik>
    </Body>
  );
};

export const PurchaseWrapper: FC = () => (
  <PurchaseProvider>{() => <Purchase />}</PurchaseProvider>
);
